import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calculator, Calendar, FileText, Sparkles, BookOpen, Wand2, ArrowRight, Radar, RefreshCw, ShieldCheck, Landmark, User, Shield, AlertTriangle, Info, Lightbulb, TrendingUp, Wallet, Percent, Receipt, PieChart } from "lucide-react";
import { toast } from "sonner";
import OnboardingWizard from "@/components/OnboardingWizard";
import NextDeadlineHero from "@/components/NextDeadlineHero";
import RegimeCheckBanner from "@/components/RegimeCheckBanner";
import ScadenzeMismatchAlert from "@/components/ScadenzeMismatchAlert";

export default function Dashboard() {
  const { user, realUser } = useAuth();
  const [deadlines, setDeadlines] = useState([]);
  const [docsCount, setDocsCount] = useState(0);
  const [bandiInfo, setBandiInfo] = useState({ unread: 0, last_refresh: null });
  const [suggestions, setSuggestions] = useState([]);
  const [fiscal, setFiscal] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Fix: guard contro loop di re-apertura per account pre-esistenti.
  // Il wizard deve auto-aprirsi UNA SOLA VOLTA per sessione. Se l'utente lo chiude
  // (skip/finish/X), non riaprirlo anche se realUser cambia in seguito.
  const onboardingAutoOpenedRef = useRef(false);
  const onboardingDismissedRef = useRef(false);

  useEffect(() => {
    if (
      realUser &&
      realUser.onboarding_completed === false &&
      !onboardingAutoOpenedRef.current &&
      !onboardingDismissedRef.current
    ) {
      onboardingAutoOpenedRef.current = true;
      setShowOnboarding(true);
    }
  }, [realUser?.id, realUser?.onboarding_completed]);

  const closeOnboarding = () => {
    onboardingDismissedRef.current = true;
    setShowOnboarding(false);
  };

  const loadAll = () => {
    api.get("/deadlines").then((r) => setDeadlines(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocsCount(r.data.length)).catch(() => {});
    api.get("/bandi/unread-count").then((r) => setBandiInfo(r.data)).catch(() => {});
    api.get("/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/fiscal-status").then((r) => setFiscal(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const refreshBandi = async () => {
    if (!user?.regione && !user?.settore) { toast.error("Completa il profilo prima"); return; }
    setRefreshing(true);
    try {
      const r = await api.post("/bandi/refresh");
      toast.success(`${r.data.new_bandi} nuovi bandi trovati`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ricerca");
    } finally { setRefreshing(false); }
  };

  const upcoming = deadlines.filter((d) => !d.completed).slice(0, 3);
  const firstName = user?.full_name?.split(" ")[0] || "";

  const tiles = [
    { to: "/app/verifica", icon: ShieldCheck, title: "Verifica Fiscale", desc: "Atteso vs pagato: controlla che tutto quadri.", color: "bg-primary/10 text-primary", tid: "tile-audit" },
    { to: "/app/chat", icon: Sparkles, title: "Chiedi al Copilota", desc: "Verifica dubbi fiscali, subito.", color: "bg-secondary/10 text-secondary", tid: "tile-chat" },
    { to: "/app/bandi", icon: Radar, title: "Radar Bandi", desc: "Opportunità aggiornate.", color: "bg-primary/10 text-primary", tid: "tile-bandi", badge: bandiInfo.unread },
    { to: "/app/movimenti", icon: Landmark, title: "Movimenti", desc: "CSV categorizzato dall'AI.", color: "bg-accent text-accent-foreground", tid: "tile-tx" },
    { to: "/app/calcolatori", icon: Calculator, title: "Calcolatori", desc: "IRPEF, IVA, ISEE, Bollo.", color: "bg-accent text-accent-foreground", tid: "tile-calc" },
    { to: "/app/scadenze", icon: Calendar, title: "Scadenze", desc: "Non farti sorprendere.", color: "bg-primary/10 text-primary", tid: "tile-deadlines" },
    { to: "/app/archivio", icon: FileText, title: "Archivio", desc: "OCR fatture e scontrini.", color: "bg-accent text-accent-foreground", tid: "tile-vault" },
    { to: "/app/guide", icon: BookOpen, title: "Guide", desc: "SPID, CIE, 730, F24.", color: "bg-secondary/10 text-secondary", tid: "tile-guides" },
    { to: "/app/generatore", icon: Wand2, title: "Generatore", desc: "Autocertificazioni.", color: "bg-primary/10 text-primary", tid: "tile-generator" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <OnboardingWizard open={showOnboarding} onClose={closeOnboarding} />
      <div className="fade-in-up">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Dashboard</div>
        <h1 className="font-heading font-bold text-4xl lg:text-5xl tracking-tighter">
          Ciao {firstName || "utente"}, <span className="text-primary">tutto sotto controllo?</span>
        </h1>
        <p className="text-muted-foreground mt-2">Verifica, monitora, non farti sorprendere.</p>
      </div>

      {/* Regime previdenziale: conferma esplicita per evitare scadenze sbagliate */}
      <RegimeCheckBanner />

      {/* Detection automatica: se le scadenze in DB non corrispondono al regime → sync 1-click */}
      <ScadenzeMismatchAlert onSynced={loadAll} />

      {/* Prossima scadenza in evidenza — con countdown live e CTA contestuale */}
      <NextDeadlineHero deadlines={deadlines} />

      {/* Hero Dipendente (visibile per utenti con reddito dipendente OR modalità admin "dipendente") */}
      {(user?.has_dipendente_income || user?.tipo_piva === "nessuna") && (
        <DipendenteHero user={user} />
      )}

      {/* Stato Fiscale P.IVA — solo se effective user è P.IVA */}
      {fiscal && fiscal.outlook && (user?.tipo_piva === "forfettario" || user?.tipo_piva === "ordinario") && <FiscalStatusHero fiscal={fiscal} />}
      {fiscal && !fiscal.outlook && (user?.tipo_piva === "forfettario" || user?.tipo_piva === "ordinario") && <FiscalStatusEmpty user={user} fiscal={fiscal} />}

      {/* Bandi hero (feature del giorno) */}
      <Card className="p-6 lg:p-8 rounded-2xl border-2 border-primary/10 bg-gradient-to-br from-accent/40 to-transparent flex items-start gap-6 flex-wrap fade-in-up" data-testid="dashboard-bandi-hero">
        <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
          <Radar className="w-7 h-7 text-primary-foreground" />
        </div>
        <div className="flex-1 min-w-[250px]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-1">Radar Bandi</div>
          <h2 className="font-heading font-bold text-2xl mb-1">
            {bandiInfo.unread > 0
              ? <>Ci sono <span className="text-primary">{bandiInfo.unread} nuovi bandi</span> per te</>
              : "Nessun nuovo bando al momento"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {user?.regione ? `Ricerca attiva per ${user.regione}${user.settore ? " · " + user.settore : ""}.` : "Completa il profilo per attivare il radar."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={refreshBandi} disabled={refreshing || (!user?.regione && !user?.settore)} className="rounded-full" data-testid="dashboard-bandi-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
          <Link to="/app/bandi">
            <Button className="rounded-full" data-testid="dashboard-bandi-open">Vedi bandi <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </Card>

      {/* Consigli per te (data completeness advisor) */}
      {suggestions.length > 0 && (
        <Card className="p-6 lg:p-8 rounded-2xl border" data-testid="dashboard-suggestions">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Consigli per te</div>
              <h2 className="font-heading font-semibold text-xl">Cosa manca per un audit completo</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {suggestions.slice(0, 4).map((s) => {
              const IconMap = { user: User, shield: ShieldCheck, landmark: Landmark, alert: AlertTriangle, info: Info, file: FileText, calendar: Calendar, radar: Radar };
              const SIcon = IconMap[s.icon] || Info;
              const priorityColor = s.priority === "high" ? "border-secondary/40 bg-secondary/5" : s.priority === "medium" ? "border-primary/20 bg-accent/30" : "border-border bg-card";
              return (
                <div key={s.id} className={`p-4 rounded-xl border-2 ${priorityColor} flex gap-3 items-start`} data-testid={`suggestion-${s.id}`}>
                  <SIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.priority === "high" ? "text-secondary" : "text-primary"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <p className="text-xs text-muted-foreground mt-1 mb-2 leading-relaxed">{s.description}</p>
                    <Link to={s.cta_link}>
                      <Button size="sm" variant="ghost" className="rounded-full h-7 -ml-2 text-primary" data-testid={`suggestion-cta-${s.id}`}>
                        {s.cta_label} <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}


      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 md:row-span-2 p-8 rounded-2xl border bg-card shadow-[0_8px_30px_rgb(0,0,0,0.04)] fade-in-up" data-testid="dashboard-deadlines-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Prossime scadenze</div>
              <h2 className="font-heading font-bold text-2xl mt-1">Cosa arriva presto</h2>
            </div>
            <Link to="/app/scadenze"><Button variant="ghost" size="sm" className="rounded-full" data-testid="dashboard-view-all-deadlines">Vedi tutte <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center border border-dashed rounded-xl">
              Nessuna scadenza attiva. <Link to="/app/scadenze" className="text-primary font-semibold" data-testid="dashboard-add-first-deadline">Aggiungi la prima</Link>.
            </div>
          ) : (
            <ul className="space-y-3">
              {upcoming.map((d) => (
                <li key={d.id} className="flex items-center justify-between p-4 rounded-xl bg-accent/50 border border-border/60" data-testid={`dashboard-deadline-${d.id}`}>
                  <div>
                    <div className="font-semibold">{d.title}</div>
                    <div className="text-xs text-muted-foreground">{d.description}</div>
                  </div>
                  <Badge variant="secondary" className="rounded-full">{new Date(d.due_date).toLocaleDateString("it-IT")}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)] fade-in-up" style={{ animationDelay: "0.1s" }} data-testid="dashboard-stats-docs">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Documenti</div>
          <div className="font-heading font-bold text-5xl mt-2 text-primary">{docsCount}</div>
          <div className="text-sm text-muted-foreground mt-1">nel tuo archivio</div>
        </Card>

        <Card className="p-6 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)] fade-in-up" style={{ animationDelay: "0.15s" }} data-testid="dashboard-stats-deadlines">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Scadenze attive</div>
          <div className="font-heading font-bold text-5xl mt-2 text-secondary">{deadlines.filter((d) => !d.completed).length}</div>
          <div className="text-sm text-muted-foreground mt-1">da gestire</div>
        </Card>
      </div>

      <div>
        <h2 className="font-heading font-bold text-2xl mb-4">Azioni rapide</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t, i) => (
            <Link key={t.to} to={t.to} data-testid={t.tid} className="fade-in-up" style={{ animationDelay: `${0.05 * i}s` }}>
              <Card className="p-6 rounded-2xl border hover:-translate-y-1 hover:shadow-lg transition-transform h-full relative">
                {t.badge > 0 && (
                  <Badge className="absolute top-3 right-3 bg-secondary text-secondary-foreground rounded-full h-5 min-w-[20px] px-1.5 text-[10px]">{t.badge}</Badge>
                )}
                <div className={`w-11 h-11 rounded-xl ${t.color} flex items-center justify-center mb-3`}>
                  <t.icon className="w-5 h-5" />
                </div>
                <div className="font-heading font-semibold text-lg">{t.title}</div>
                <div className="text-sm text-muted-foreground">{t.desc}</div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Fiscal Status sub-components -------------------- */

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const euroDec = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function FiscalStatusEmpty({ user, fiscal }) {
  return (
    <Card className="p-6 lg:p-8 rounded-2xl border-2 border-dashed border-primary/20 bg-accent/20" data-testid="fiscal-empty">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <PieChart className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-1">Stato Fiscale {fiscal.year}</div>
          <h2 className="font-heading font-bold text-2xl mb-2">Costruiamo il tuo quadro fiscale.</h2>
          <p className="text-sm text-muted-foreground max-w-xl">
            Per vedere qui il tuo fatturato dell'anno e sapere quanto e quando pagherai (INPS, IRPEF, IVA),
            {!user?.tipo_piva || user.tipo_piva === "nessuna" ? " indica il tuo regime fiscale nel profilo, poi " : " "}
            carica movimenti/fatture o compila il piano fiscale.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/movimenti"><Button variant="outline" className="rounded-full" data-testid="fiscal-empty-tx"><Landmark className="w-4 h-4 mr-2" /> Importa movimenti</Button></Link>
          <Link to="/app/verifica"><Button className="rounded-full" data-testid="fiscal-empty-piano"><ShieldCheck className="w-4 h-4 mr-2" /> Compila piano</Button></Link>
        </div>
      </div>
    </Card>
  );
}

function FiscalStatusHero({ fiscal }) {
  const o = fiscal.outlook;
  const dovuto = fiscal.dovuto_ytd;
  const prox = fiscal.prossime_per_voce || {};
  const isProjection = o.source === "stima_automatica";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—";
  return (
    <Card className="rounded-2xl border-2 border-primary/15 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 overflow-hidden fade-in-up" data-testid="fiscal-hero">
      {/* Header */}
      <div className="px-6 lg:px-8 pt-6 pb-4 border-b border-border/50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-1">Stato Fiscale {fiscal.year}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-bold text-xl">
              {(o.regime === "forfettario" && "Regime Forfettario") || (o.regime === "ordinario" && "Regime Ordinario") || "Dipendente"}
            </span>
            {fiscal.is_new_business && (
              <Badge className="text-[10px] rounded-full bg-secondary/10 text-secondary hover:bg-secondary/10">Prima attività (no acconto)</Badge>
            )}
          </div>
        </div>
        <Link to="/app/verifica"><Button variant="ghost" size="sm" className="rounded-full text-xs" data-testid="fiscal-hero-piano-link">{fiscal.has_piano ? "Aggiorna piano" : "Personalizza piano"} <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
      </div>

      {/* Sezione 1: AD OGGI (dati reali YTD) */}
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Ad oggi</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FiscalStat
            icon={TrendingUp}
            label="Incassato"
            value={euro(fiscal.ricavi_ytd)}
            sublabel="Da movimenti bancari (principio di cassa)"
            color="primary"
            bold
            testid="fiscal-incassato-ytd"
          />
          <FiscalStat icon={Wallet} label="Spese scaricabili" value={euro(fiscal.costi_ytd)} sublabel={o.regime === "forfettario" ? "Non deducibili nel forfettario" : "Deducibili dal reddito"} testid="fiscal-costi-ytd" />
          <FiscalStat icon={Receipt} label="Tasse già versate" value={euro(fiscal.tasse_pagate_ytd)} sublabel="F24, INPS, IVA pagati" testid="fiscal-tasse-pagate" />
        </div>
        {/* Miglioramento 3: card fatturato emesso da XML — DISTINTA da "Incassato". Le stime d'imposta restano
            sui movimenti (principio di cassa forfettario, corretto).  */}
        {fiscal.fatture_emesse_count > 0 && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            <FiscalStat
              icon={FileText}
              label="Fatturato emesso"
              value={euro(fiscal.fatture_emesse_totale)}
              sublabel={
                fiscal.fatture_da_incassare_totale > 0
                  ? `${fiscal.fatture_incassate_count}/${fiscal.fatture_emesse_count} incassate · ${euro(fiscal.fatture_da_incassare_totale)} in attesa`
                  : `${fiscal.fatture_emesse_count} fatture · tutte incassate ✓`
              }
              color="secondary"
              testid="fiscal-fatturato-emesso"
            />
          </div>
        )}
      </div>

      {/* Sezione 2: DA VERSARE (calcolato sui dati reali YTD, con date) */}
      {dovuto && (
        <div className="px-6 lg:px-8 pt-5 pb-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Da versare — in base ai dati caricati finora</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ObligoRow icon={Wallet} label="Contributi previdenziali" value={euro(dovuto.inps_atteso)} data={fmtDate(prox.INPS)} nota={(dovuto.inps_nota || "").split("—")[0].trim()} color="primary" testid="obligo-inps" />
            <ObligoRow icon={Percent} label={o.regime === "forfettario" ? "Imposta sostitutiva" : "IRPEF + addizionali"} value={euro(dovuto.imposta_attesa)} data={fmtDate(prox.IRPEF)} color="secondary" testid="obligo-imposta" />
            {dovuto.iva_attesa > 0 ? (
              <ObligoRow icon={Receipt} label="IVA a debito" value={euro(dovuto.iva_attesa)} data={fmtDate(prox.IVA)} color="secondary" testid="obligo-iva" />
            ) : (
              <ObligoRow icon={Wallet} label="Netto stimato" value={euro(dovuto.netto_stimato)} data="dopo tasse" color="primary" testid="obligo-netto" />
            )}
          </div>
        </div>
      )}

      {/* Sezione 3: PREVISIONE ANNO (annualizzata, chiaramente marcata) */}
      <div className="px-6 lg:px-8 pt-2 pb-6">
        <div className="rounded-2xl bg-accent/40 border p-4 md:p-5 grid md:grid-cols-3 gap-4 items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-3.5 h-3.5 text-secondary" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-secondary">Previsione fine anno</span>
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              {isProjection
                ? `Se andrà così tutto il ${fiscal.year} (proiezione dai primi ${o.months_elapsed || 1} mesi)`
                : `Sul piano che hai salvato in Verifica Fiscale`}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Totale {fiscal.year}</div>
            <div className="font-heading font-bold text-2xl" data-testid="fiscal-totale-anno">{euro(o.totale_atteso)}</div>
            <div className="text-[10px] text-muted-foreground">Imposte + contributi + IVA</div>
          </div>
          <div>
            {fiscal.is_new_business ? (
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                <Info className="inline w-3 h-3 mr-1 mb-0.5 text-primary" />
                Prima attività: no acconti. Solo saldo a giugno {fiscal.year + 1}.
              </div>
            ) : fiscal.acconto_next_year > 0 ? (
              <>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">+ Acconto {fiscal.year + 1}</div>
                <div className="font-heading font-semibold text-lg">{euro(fiscal.acconto_next_year)}</div>
                <div className="text-[10px] text-muted-foreground leading-tight">
                  {/* Fix: label letta dal backend (single source of truth con fiscal_constants) */}
                  {fiscal.acconto_split?.label
                    ? `${fiscal.acconto_split.label} ${fiscal.year + 1}`
                    : `50% a giugno + 50% a novembre ${fiscal.year + 1}`}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ObligoRow({ icon: Icon, label, value, data, nota, color = "primary", testid }) {
  return (
    <div className="rounded-xl border bg-background/60 p-3.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-heading font-bold text-xl ${color === "primary" ? "text-primary" : "text-secondary"}`}>{value}</div>
      <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
        <span className="truncate flex-1">{nota || "—"}</span>
        <span className="font-semibold whitespace-nowrap ml-2">{data}</span>
      </div>
    </div>
  );
}

function FiscalStat({ icon: Icon, label, value, sublabel, color = "primary", bold = false, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-heading font-bold ${bold ? "text-3xl" : "text-2xl"} ${color === "primary" ? "text-primary" : color === "secondary" ? "text-secondary" : ""}`}>{value}</div>
      {sublabel && <div className="text-[10px] text-muted-foreground leading-tight">{sublabel}</div>}
    </div>
  );
}

function DipendenteHero({ user }) {
  const [bp, setBp] = useState(null);
  const [auu, setAuu] = useState(null);
  const [asilo, setAsilo] = useState(null);
  const [bmam, setBmam] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback: se non c'è RAL nel profilo, usa un esempio a 30k per mostrare comunque il pannello
  const ralEffettivo = Number(user?.ral_lordo) > 0 ? Number(user.ral_lordo) : 30000;
  const isExample = !(Number(user?.ral_lordo) > 0);

  useEffect(() => {
    const fetchAll = async () => {
      const tasks = [];
      tasks.push(api.post("/calc/busta-paga", {
        ral: ralEffettivo,
        mensilita: user?.mensilita || 13,
        tipo_contratto: user?.tipo_contratto || "indeterminato",
        aliquota_add_regionale: user?.aliquota_add_regionale ?? null,
        aliquota_add_comunale: user?.aliquota_add_comunale ?? null,
      }).then((r) => setBp(r.data)).catch(() => {}));

      if (user?.figli_a_carico?.length > 0) {
        tasks.push(api.post("/calc/assegno-unico", {
          figli: user.figli_a_carico,
          isee: user.isee || null,
          madre_under_21: !!user.madre_under_21,
          entrambi_genitori_lavoratori: !!user.entrambi_genitori_lavoratori,
        }).then((r) => setAuu(r.data)).catch(() => {}));

        const hasSmallKid = user.figli_a_carico?.some((f) => f.eta < 3);
        if (hasSmallKid) {
          tasks.push(api.post("/calc/bonus-asilo", { isee: user.isee || null })
            .then((r) => setAsilo(r.data)).catch(() => {}));
        }
        if (user.figli_a_carico.length >= 2) {
          const eta_ultimo = Math.min(...user.figli_a_carico.map((f) => f.eta));
          tasks.push(api.post("/calc/bonus-mamma", {
            n_figli: user.figli_a_carico.length,
            eta_ultimo_figlio: eta_ultimo,
            reddito_annuo: ralEffettivo,
          }).then((r) => setBmam(r.data)).catch(() => {}));
        }
      }

      await Promise.all(tasks);
      setLoading(false);
    };
    fetchAll();
  }, [ralEffettivo, JSON.stringify(user?.figli_a_carico), user?.isee]);

  const now = new Date();
  const mesePassati = now.getMonth() + 1;
  const ralYtd = (ralEffettivo * mesePassati) / 12;

  return (
    <Card className="p-6 lg:p-8 rounded-2xl border-2 border-secondary/10 bg-gradient-to-br from-secondary/5 to-transparent fade-in-up" data-testid="dipendente-hero">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-1">La tua busta paga</div>
          <h2 className="font-heading font-bold text-2xl">Cosa hai in mano (e cosa ti spetta)</h2>
          {isExample && (
            <div className="mt-2 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              Esempio con RAL €30.000 — <Link to="/app/impostazioni" className="underline font-semibold">compila il tuo profilo</Link>
            </div>
          )}
        </div>
        <Link to="/app/calcolatori" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1" data-testid="dipendente-hero-details">
          Dettagli calcolatori <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading && !bp ? (
        <div className="text-sm text-muted-foreground py-6">Calcolo in corso...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <FiscalStat icon={Wallet} label="RAL 2026" value={`€ ${ralEffettivo.toLocaleString("it-IT")}`} sublabel={`YTD ~€ ${Math.round(ralYtd).toLocaleString("it-IT")}`} color="secondary" bold testid="dip-ral" />
          {bp && (
            <>
              <FiscalStat icon={TrendingUp} label="Netto mensile" value={`€ ${bp.netto_mensile.toLocaleString("it-IT")}`} sublabel={`× ${bp.mensilita} mensilità = € ${Math.round(bp.netto_annuo).toLocaleString("it-IT")}`} color="primary" bold testid="dip-netto" />
              <FiscalStat icon={Receipt} label="IRPEF netta anno" value={`€ ${Math.round(bp.irpef_netta).toLocaleString("it-IT")}`} sublabel={`Detrazione ${Math.round(bp.detrazione_lavoro.importo)} €`} testid="dip-irpef" />
              <FiscalStat icon={Percent} label="Trattamento Integrativo" value={bp.trattamento_integrativo.importo > 0 ? `€ ${bp.trattamento_integrativo.importo}` : "Non spetta"} sublabel={bp.trattamento_integrativo.importo > 0 ? "Ex bonus Renzi attivo" : bp.trattamento_integrativo.motivo?.slice(0, 40)} testid="dip-ti" />
            </>
          )}
        </div>
      )}

      {(auu || asilo || bmam) ? (
        <div className="border-t border-border pt-5">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Bonus famiglia stimati</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {auu && auu.totale_mensile > 0 && (
              <div className="rounded-xl border bg-background p-4" data-testid="dip-bonus-auu">
                <div className="text-[11px] font-bold uppercase text-primary mb-1">Assegno Unico</div>
                <div className="text-2xl font-heading font-bold">€ {auu.totale_mensile}<span className="text-xs text-muted-foreground font-normal">/mese</span></div>
                <div className="text-xs text-muted-foreground mt-1">€ {Math.round(auu.totale_annuo).toLocaleString("it-IT")}/anno · {user.figli_a_carico?.length || 0} figli</div>
              </div>
            )}
            {asilo && (
              <div className="rounded-xl border bg-background p-4" data-testid="dip-bonus-asilo">
                <div className="text-[11px] font-bold uppercase text-primary mb-1">Bonus Asilo</div>
                <div className="text-2xl font-heading font-bold">€ {asilo.importo_annuo}<span className="text-xs text-muted-foreground font-normal">/anno</span></div>
                <div className="text-xs text-muted-foreground mt-1">{asilo.fascia}</div>
              </div>
            )}
            {bmam?.spetta && (
              <div className="rounded-xl border bg-background p-4" data-testid="dip-bonus-mamma">
                <div className="text-[11px] font-bold uppercase text-primary mb-1">Bonus Mamma</div>
                <div className="text-2xl font-heading font-bold">€ {bmam.importo_annuo}<span className="text-xs text-muted-foreground font-normal">/anno</span></div>
                <div className="text-xs text-muted-foreground mt-1">{bmam.erogazione?.split(" tramite")[0]}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border-t border-border pt-4 text-xs text-muted-foreground flex items-start gap-2">
          <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div>
            Aggiungi i tuoi <strong>figli a carico</strong> in <Link to="/app/impostazioni" className="text-primary underline font-semibold">Impostazioni</Link> per vedere qui Assegno Unico, Bonus Mamma e Bonus Asilo stimati.
          </div>
        </div>
      )}

      <div className="border-t border-border pt-4 mt-5">
        <div className="flex items-start gap-3 text-xs text-muted-foreground">
          <Info className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-foreground">Prossime scadenze rilevanti:</strong> giugno rimborso 730 in busta paga · dicembre erogazione tredicesima e Bonus Mamma · settembre acconto IRPEF (se dovuto).
          </div>
        </div>
      </div>
    </Card>
  );
}
